<template>
  <!-- Map 上の Popup -->
  <div
    ref="popup"
    class="popup-box"
    v-if="popup"
    v-bind:style="{ transform: `translate(${popup.x}px, ${popup.y}px)` }"
  >
    <!-- Popup コンテンツ -->
    <map-popup class="popup" v-on:resize="$_onResize">
      <template v-slot:header>
        <div class="none" />
      </template>
      <template v-slot:body>
        <div class="body">
          <span>{{ time }}</span>
        </div>
      </template>
      <template v-slot:footer>
        <div class="none" />
      </template>
    </map-popup>
  </div>
</template>

<script>
import moment from 'moment'
import MapPopup from '@/components/map/AppMapPopup.vue'
import { mapGetters } from 'vuex'
import { DATE_FORMAT_HYPHEN, TIME_FORMAT_MILLISECOND } from '@/constants.js'

// 日時フォーマット
const DATETIME_FORMAT = `${DATE_FORMAT_HYPHEN} ${TIME_FORMAT_MILLISECOND}`

/**
 * Map 上の 位置履歴 Popup コンテンツ (AppMapControl と同じ <div> 内に入れる)
 */
export default {
  name: 'AppMapPopupTrackPoint',
  components: {
    'map-popup': MapPopup,
  },
  props: {
    /**
     * マップコンポーネント
     */
    currentMapComponent: {
      type: Object,
      default: null,
    },
    /**
     * マップのClientRect
     */
    mapClientRect: {
      type: [Object, DOMRect],
      default: null,
    },
    /**
     * 地物
     */
    feature: {
      type: Object,
      default: null,
    },
    /**
     * オフセットのデフォルト値
     */
    defaultOffset: {
      type: Object,
      default: () => ({ x: 16, y: -8 }),
    },
  },
  data: function () {
    return {
      popup: null,
      clientRect: null,
      offset: this.defaultOffset,
    }
  },
  computed: {
    ...mapGetters('map', ['center', 'zoom', 'heading']),
    /**
     * 自身の影響のみのoffset値
     * @type {Object}
     */
    selfOffset: function () {
      const offset = {
        x: this.defaultOffset.x || 0,
        y: this.defaultOffset.y || 0,
      }
      if (!this.clientRect) {
        return offset
      }

      offset.y -= this.clientRect.height
      return offset
    },
    /**
     * 表示時刻
     * @type {String}
     */
    time: function () {
      if (!this.feature) return ''
      return moment(this.feature.time).format(DATETIME_FORMAT)
    },
  },
  watch: {
    /**
     * [Watch] マップの中心
     */
    center() {
      this.$_onUpdateMapState()
    },
    /**
     * [Watch] マップのズーム
     */
    zoom() {
      this.$_onUpdateMapState()
    },
    /**
     * [Watch] マップの回転角度
     */
    heading() {
      this.$_onUpdateMapState()
    },
    /**
     * 地物
     */
    feature: function () {
      this.$log.debug('[hoverFeature] watch', this.feature)
      this.renderPopup()
    },
  },
  methods: {
    /**
     * ポップアップを描画
     */
    renderPopup() {
      if (!this.feature) {
        // データなし ⇒ 非表示にする
        this.popup = null
        return
      }

      // Popup の表示位置の取得
      if (!this.currentMapComponent) {
        this.$log.warn('[render] Popup required currentMapComponent is null')
        return
      }
      const point = this.currentMapComponent.fromLatlngtoPixel(
        this.feature.lat,
        this.feature.lng
      )
      const value = {
        ...this.feature,
        x: point.x + this.selfOffset.x,
        y: point.y + this.selfOffset.y,
      }

      if (!this.popup || this.popup.x !== value.x || this.popup.y !== value.y) {
        // 変更がある時のみ
        this.popup = value
      }
    },

    /**
     * Map の状態が更新された
     */
    $_onUpdateMapState() {
      this.renderPopup()
    },

    /**
     * リサイズイベント
     */
    $_onResize: function (clientRect) {
      this.clientRect = clientRect
      this.renderPopup()
    },

    /**
     * Popup を Hide
     */
    $_hide() {
      this.popupData = null
      this.renderPopup()
    },
  },
}
</script>

<style lang="scss" scoped>
.popup-box {
  width: auto;
  position: absolute;
  // interleaved: false によって UI が deck.gl の canvas （z-index: 999）より背面に配置される問題への対応。
  z-index: 1000;
}

.none {
  height: 0;
  display: none;
}

.popup {
  border-radius: 4px;
  padding: 4px 16px;
  box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.2);

  ::v-deep .popup-tip {
    display: none;
  }

  ::v-deep .hr {
    height: 0;
    display: none;
  }

  ::v-deep .icon-close {
    display: none;
  }
}

.body {
  font-size: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>
